import { useQuery } from "@tanstack/react-query";
import { Loader2, Building2, TrendingUp, Trophy, Users2 } from "lucide-react";
import api from "@/lib/api";
import { formatINRShort } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const AXIS = { fontSize: 10, fill: "#94a3b8" };
const TIP = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <Card className="p-4 shadow-none border-slate-200/80" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-400">
        <Icon className="h-3.5 w-3.5" />
        <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</p>
    </Card>
  );
}

export default function DealerInsights() {
  const { data, isLoading } = useQuery({
    queryKey: ["dealer-insights"],
    queryFn: async () => (await api.get("/team/dealer-insights")).data,
  });
  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const dealers = data?.dealers || [];
  const top = dealers.slice(0, 10);
  const pipeline = dealers.reduce((a, d) => a + d.opp_value, 0);
  const won = dealers.reduce((a, d) => a + d.won_value, 0);
  const leadsN = dealers.reduce((a, d) => a + d.leads, 0);

  return (
    <div data-testid="dealer-insights-page">
      <PageHeader title="Dealer BI — Business Insights" subtitle="Dealer-wise lead & opportunity performance across the CRM." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Kpi label="Dealers Involved" value={dealers.length} icon={Building2} testid="di-kpi-dealers" />
        <Kpi label="Leads via Dealers" value={leadsN} icon={Users2} testid="di-kpi-leads" />
        <Kpi label="Opportunity Value" value={formatINRShort(pipeline)} icon={TrendingUp} testid="di-kpi-pipeline" />
        <Kpi label="Won via Dealers" value={formatINRShort(won)} icon={Trophy} testid="di-kpi-won" />
      </div>

      {top.length > 0 && (
        <Card className="p-4 mb-5 shadow-none border-slate-200/80" data-testid="di-chart">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Top Dealers by Value in CRM</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={top} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="dealer" tick={AXIS} axisLine={false} tickLine={false} interval={0} angle={-20} height={50} textAnchor="end" />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => formatINRShort(v)} />
              <Tooltip contentStyle={TIP} formatter={(v) => formatINRShort(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="lead_value" name="Lead value" fill="#0ea5e9" radius={[3, 3, 0, 0]} maxBarSize={24} />
              <Bar dataKey="opp_value" name="Opportunity value" fill="#334155" radius={[3, 3, 0, 0]} maxBarSize={24} />
              <Bar dataKey="won_value" name="Won value" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card className="p-4 shadow-none border-slate-200/80 overflow-hidden" data-testid="di-table">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">All Dealers ({dealers.length})</p>
        {dealers.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No leads or opportunities have a dealer involved yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-1.5 pr-3">Dealer</th>
                  <th className="pr-3 text-right">Leads</th><th className="pr-3 text-right">Lead Value</th>
                  <th className="pr-3 text-right">Opportunities</th><th className="pr-3 text-right">Opp Value</th>
                  <th className="pr-3 text-right">Won</th><th className="text-right">Won Value</th>
                </tr>
              </thead>
              <tbody>
                {dealers.map((d, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`di-row-${i}`}>
                    <td className="py-1.5 pr-3 font-medium text-slate-800">{d.dealer}</td>
                    <td className="pr-3 text-right">{d.leads}</td>
                    <td className="pr-3 text-right font-mono">{d.lead_value ? formatINRShort(d.lead_value) : "—"}</td>
                    <td className="pr-3 text-right">{d.opps}</td>
                    <td className="pr-3 text-right font-mono">{d.opp_value ? formatINRShort(d.opp_value) : "—"}</td>
                    <td className="pr-3 text-right">{d.won}</td>
                    <td className="text-right font-mono text-emerald-700">{d.won_value ? formatINRShort(d.won_value) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
